import { useHash } from '@starryui10/hooks/useHash';
import { fontTypeToObject } from '@starryui10/theme/font';
import React, { HTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';
import { color as colour } from '@starryui10/theme/color';
import { Property as CSS } from 'csstype';

type RenderAs = 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'strong' | 'small';

interface ITextProps extends global.HashableComponent, HTMLAttributes<HTMLElement> {
    variant?: theme.FontType;
    renderAs?: RenderAs;
    color?: string;
    children: string | ReactNode;
    inline?: boolean;
    textAlign?: CSS.TextAlign;
}

const reset: React.CSSProperties = {
    padding: 0,
    margin: 0,
};

const Paragraph = styled.p<{ prop: React.CSSProperties }>`
    ${(props) => ({ ...props.prop })}
`;

const Span = styled.span<{ prop: React.CSSProperties }>`
    ${(props) => ({ ...props.prop })}
`;

const Small = styled.small<{ prop: React.CSSProperties }>`
    ${(props) => ({ ...props.prop })}
`;

const Strong = styled.strong<{ prop: React.CSSProperties }>`
    ${(props) => ({ ...props.prop })}
`;

const Heading1 = styled.h1<{ prop: React.CSSProperties }>`
    ${(props) => ({ ...props.prop })}
`;

const Heading2 = styled.h2<{ prop: React.CSSProperties }>`
    ${(props) => ({ ...props.prop })}
`;

const Heading3 = styled.h3<{ prop: React.CSSProperties }>`
    ${(props) => ({ ...props.prop })}
`;

const Heading4 = styled.h4<{ prop: React.CSSProperties }>`
    ${(props) => ({ ...props.prop })}
`;

const Heading5 = styled.h5<{ prop: React.CSSProperties }>`
    ${(props) => ({ ...props.prop })}
`;

const Heading6 = styled.h6<{ prop: React.CSSProperties }>`
    ${(props) => ({ ...props.prop })}
`;

const Text: React.FC<ITextProps> = ({
    inline = false,
    variant = 'normal.regular',
    renderAs = 'span',
    color = colour.Grey.G80,
    children,
    addHash,
    textAlign,
    ...props
}) => {
    const styles: React.CSSProperties = {
        ...reset,
        ...fontTypeToObject(variant),
        color,
    };

    // removes the line-height useful if you want to display the text vertically aligned
    if (inline) {
        styles.lineHeight = '0';
    }

    if (textAlign) {
        styles.textAlign = textAlign;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { ref } = useHash<any>(addHash);

    switch (renderAs) {
        case 'p':
            return (
                <Paragraph ref={ref} prop={styles} {...props}>
                    {children}
                </Paragraph>
            );
        case 'h1':
            return (
                <Heading1 ref={ref} prop={styles} {...props}>
                    {children}
                </Heading1>
            );
        case 'h2':
            return (
                <Heading2 ref={ref} prop={styles} {...props}>
                    {children}
                </Heading2>
            );
        case 'h3':
            return (
                <Heading3 ref={ref} prop={styles} {...props}>
                    {children}
                </Heading3>
            );
        case 'h4':
            return (
                <Heading4 ref={ref} prop={styles} {...props}>
                    {children}
                </Heading4>
            );
        case 'h5':
            return (
                <Heading5 ref={ref} prop={styles} {...props}>
                    {children}
                </Heading5>
            );
        case 'h6':
            return (
                <Heading6 ref={ref} prop={styles} {...props}>
                    {children}
                </Heading6>
            );
        case 'strong':
            return (
                <Strong ref={ref} prop={styles} {...props}>
                    {children}
                </Strong>
            );
        case 'small':
            return (
                <Small ref={ref} prop={styles} {...props}>
                    {children}
                </Small>
            );
        default:
            return (
                <Span ref={ref} prop={styles} {...props}>
                    {children}
                </Span>
            );
    }
};

export { Text };
export default Text;
